/**
 * PWA Install Prompt & Offline Indicator
 * Shows install button and offline status for the Progressive Web App
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Download, Wifi, WifiOff, X, Smartphone, Monitor, CloudOff, RefreshCw } from 'lucide-react';
import { Button } from './button';
import { toast } from 'sonner';

// Store the deferred prompt event
let deferredPrompt = null;

export const PWAInstallPrompt = ({ variant = 'banner' }) => {
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check localStorage for dismissal
    const dismissedTime = localStorage.getItem('pwa-install-dismissed');
    if (dismissedTime && Date.now() - parseInt(dismissedTime) < 7 * 24 * 60 * 60 * 1000) {
      setDismissed(true);
    }

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      deferredPrompt = e;
      setCanInstall(true);
    };

    // Listen for successful install
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
      deferredPrompt = null;
      toast.success('App installed successfully!');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success('Installing app...');
    }
    deferredPrompt = null;
    setCanInstall(false);
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  if (isInstalled || dismissed || !canInstall) return null;

  if (variant === 'button') {
    return (
      <Button
        onClick={handleInstall}
        size="sm"
        variant="outline"
        className="gap-2"
        data-testid="pwa-install-button"
      >
        <Download className="h-4 w-4" />
        Install App
      </Button>
    );
  }

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-card border border-border rounded-lg shadow-lg p-4 z-50 animate-in slide-in-from-bottom-4"
      data-testid="pwa-install-banner"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 p-2 bg-accent/10 rounded-lg">
          <Smartphone className="h-6 w-6 text-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-sm">Install Simplif-i</h4>
          <p className="text-xs text-muted-foreground mt-1">
            Add to your home screen for quick access & offline viewing
          </p>
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" onClick={handleInstall} className="gap-1.5">
              <Download className="h-3.5 w-3.5" />
              Install
            </Button>
            <Button size="sm" variant="ghost" onClick={handleDismiss}>
              Not now
            </Button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 hover:bg-muted rounded"
        >
          <X className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>
    </div>
  );
};

// Offline indicator component
export const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowBanner(true);
      setTimeout(() => setShowBanner(false), 3000);
      toast.success('Back online');
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowBanner(true);
      toast.warning('You are offline. Some features may be limited.');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!showBanner && isOnline) return null;

  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-50 py-2 px-4 text-center text-sm font-medium transition-colors ${
        isOnline 
          ? 'bg-emerald-500 text-white' 
          : 'bg-amber-500 text-white'
      }`}
      data-testid="offline-indicator"
    >
      <div className="flex items-center justify-center gap-2">
        {isOnline ? (
          <>
            <Wifi className="h-4 w-4" />
            <span>Back online</span>
          </>
        ) : (
          <>
            <WifiOff className="h-4 w-4" />
            <span>You're offline - viewing cached content</span>
          </>
        )}
      </div>
    </div>
  );
};

// Service worker update prompt
export const UpdatePrompt = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          newWorker?.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              setWaitingWorker(newWorker);
              setShowUpdate(true);
            }
          });
        });
      });
    }
  }, []);

  const handleUpdate = () => {
    if (waitingWorker) {
      waitingWorker.postMessage('skipWaiting');
      window.location.reload();
    }
  };

  if (!showUpdate) return null;

  return (
    <div 
      className="fixed bottom-4 right-4 bg-card border border-border rounded-lg shadow-lg p-4 z-50 max-w-sm animate-in slide-in-from-bottom-4"
      data-testid="update-prompt"
    >
      <div className="flex items-start gap-3">
        <RefreshCw className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="font-semibold text-sm">Update Available</h4>
          <p className="text-xs text-muted-foreground mt-1">
            A new version is ready. Refresh to update.
          </p>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={handleUpdate}>
              Update Now
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowUpdate(false)}>
              Later
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Hook to cache documents for offline viewing
export const useOfflineDocuments = () => {
  const cacheDocument = useCallback((documentId, url) => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CACHE_DOCUMENT',
        documentId,
        url
      });
    }
  }, []);

  const cacheDocumentsBatch = useCallback((documents) => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CACHE_DOCUMENTS_BATCH',
        documents
      });
    }
  }, []);

  const getOfflineDocuments = useCallback(() => {
    return new Promise((resolve) => {
      if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
        resolve([]);
        return;
      }

      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data.offlineUrls || []);
      };

      navigator.serviceWorker.controller.postMessage(
        { type: 'GET_OFFLINE_DOCUMENTS' },
        [messageChannel.port2]
      );
    });
  }, []);

  const clearOfflineCache = useCallback(() => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CLEAR_DOCUMENT_CACHE'
      });
    }
  }, []);

  return {
    cacheDocument,
    cacheDocumentsBatch,
    getOfflineDocuments,
    clearOfflineCache
  };
};

export default PWAInstallPrompt;
